import { FieldDef } from "Pages/Setup/Connect/connectShared";

/* ---------- connection fields (wired to /api/configuration) ---------- */

export type SecondarySystemKey = "cmic" | "mrix" | "spectrum" | "jde";

const RAMP_BASE_FIELDS: FieldDef[] = [
    {
        suffix: "ramp.cmic.client_id",
        label: "Ramp Client ID",
        help: "Copy from the app you create in demo.ramp.com.",
    },
    {
        suffix: "ramp.cmic.client_secret",
        label: "Ramp Client Secret",
        help: "Copy from the app you create in demo.ramp.com. Stored obscured.",
        secret: true,
    },
    {
        suffix: "ramp.api.base_url",
        label: "Base URL",
        help: "Ramp Developer API base URL. Leave blank to use the default (https://demo-api.ramp.com).",
    },
    {
        suffix: "ramp.ui_url",
        label: "Ramp UI URL",
        help: "Your Ramp web app URL (e.g. https://demo.ramp.com). Used for deep links and the OAuth consent screen.",
    },
    {
        suffix: "ramp.webhook_secret",
        label: "Webhook Signing Secret",
        help: "Secret Ramp uses to sign inbound webhooks. Dimension verifies each event against it. Stored obscured.",
        secret: true,
    },
];

export function getRampFields(system: SecondarySystemKey): FieldDef[] {
    return RAMP_BASE_FIELDS.map((field) => ({
        ...field,
        suffix: field.suffix.replace("ramp.cmic.", `ramp.${system}.`),
    }));
}

export function getRampOauthRefreshTokenSuffix(system: SecondarySystemKey): string {
    return `ramp.${system}.oauth_refresh_token`;
}

export const RAMP_FIELDS: FieldDef[] = getRampFields("cmic");

export const CMIC_FIELDS: FieldDef[] = [
    {
        suffix: "cmic.api.oauth.baseUrl",
        label: "OAuth API URL",
        help: "Your full CMiC OAuth API URL (e.g. https://atlas-api-oauth.cmiccloud.com/cmicprod).",
        required: true,
    },
    {
        suffix: "cmic.api.oauth.cmicuser",
        label: "Service User",
        help: "Enter the service user's Client ID and Username. Saved as Client ID||Username.",
        required: true,
    },
];

export const MRI_FIELDS: FieldDef[] = [];

export const SPECTRUM_FIELDS: FieldDef[] = [];

export const JDE_FIELDS: FieldDef[] = [];

/* ---------- map data: posting defaults (step 3) ---------- */

export const POSTING_FIELDS: FieldDef[] = [
    {
        suffix: "integration.cmic.jobcost_journal",
        label: "Job cost journal",
        help: "Journal used for job cost postings. (Usually JC)",
    },
    {
        suffix: "integration.cmic.general_journal",
        label: "General journal",
        help: "Journal used for general/direct postings. (Usually GJ)",
    },
    {
        suffix: "integration.cmic.equipment_journal",
        label: "Equipment cost journal",
        help: "Journal used for equipment cost postings. Ignore if you are not using equipment costing. (Usually EC)",
    },
    {
        suffix: "integration.cmic.liability_dept_code",
        label: "Liability department code",
        help: "The code of the department that maps to the liability accounts used for expenses and bills. (Usually 00)",
    }
];

export const KEY_PLAYERS_FIELDS: FieldDef[] = [
    {
        suffix: "integration.cmic.job_approver_codes",
        label: "Job approver roles",
        help: "Pipe-separated list in the format CODE.Name|CODE2.Name2 used to map CMiC key players as Ramp job approvers.",
    },
];

export const REIMBURSEMENT_VENDOR_FIELDS: FieldDef[] = [
    {
        suffix: "integration.cmic.default_reimbursement_vendor_code",
        label: "Default reimbursement vendor code",
        help: "Vendor code used as the default vendor on reimbursement vouchers. This vendor must exist in every synced CMiC company.",
        required: true
    },
];

/* ---------- endpoint tests (static — visual parity with mockup) ---------- */

export interface TestRow {
    name: string;
    endpath: string;
    result: string;
    state: "pass" | "fail" | "wait";
}
export interface TestGroup {
    title: string;
    warn?: boolean;
    rows: TestRow[];
}

export const RAMP_TESTS: TestGroup[] = [
    {
        title: "Ramp Developer API",
        rows: [
            { name: "Test Load Entities from Ramp", endpath: "developer/v1/entities", result: "Not run", state: "wait" },
            { name: "Test Load Transactions from Ramp", endpath: "developer/v1/transactions", result: "Not run", state: "wait" },
            { name: "Test Load Reimbursements from Ramp", endpath: "developer/v1/reimbursements", result: "Not run", state: "wait" },
            { name: "Test Load Bills from Ramp", endpath: "developer/v1/bills", result: "Not run", state: "wait" },
            { name: "Test Load Vendors from Ramp", endpath: "developer/v1/vendors", result: "Not run", state: "wait" },
        ],
    },
];

export const CMIC_TESTS: TestGroup[] = [
    {
        title: "CMiC REST API",
        rows: [
            { name: "Test Load Companies from CMiC", endpath: "glrestapi/rest/v1/glcompany", result: "Not run", state: "wait" },
            { name: "Test Load Departments from CMiC", endpath: "glrestapi/rest/v1/rtigenericgldeptst", result: "Not run", state: "wait" },
            { name: "Test Load Jobs from CMiC", endpath: "jc-rest-api/rest/1/jcjob", result: "Not run", state: "wait" },
            { name: "Test Load Transactions from CMiC", endpath: "jc-rest-api/rest/1/jctran", result: "Not run", state: "wait" },
            { name: "Test Load Vouchers from CMiC", endpath: "ap-rest-api/rest/1/apallvouchers", result: "Not run", state: "wait" },
            { name: "Test Load Vendors from CMiC", endpath: "ap-rest-api/rest/1/apvendor", result: "Not run", state: "wait" },
            { name: "Test Load Projects from CMiC", endpath: "pm-rest-api/rest/1/pmproject", result: "Not run", state: "wait" },
        ],
    },
];

export const MRI_TESTS: TestGroup[] = [
    {
        title: "MRI tests",
        rows: [
            {
                name: "MRI test endpoints",
                endpath: "Coming soon",
                result: "Not run",
                state: "wait",
            },
        ],
    },
];

export const SPECTRUM_TESTS: TestGroup[] = [
    {
        title: "Spectrum tests",
        rows: [
            {
                name: "Spectrum test endpoints",
                endpath: "Coming soon",
                result: "Not run",
                state: "wait",
            },
        ],
    },
];

export const JDE_TESTS: TestGroup[] = [
    {
        title: "JDE tests",
        rows: [
            {
                name: "JDE test endpoints",
                endpath: "Coming soon",
                result: "Not run",
                state: "wait",
            },
        ],
    },
];

/* ---------- Billy fields (Billy system connect) ---------- */
export const BILLY_FIELDS: FieldDef[] = [
    {
        suffix: "billy.api.base_url",
        label: "Billy API Base URL",
        help: "Leave blank to use the default (https://app.billyforinsurance.com/api/v1). Only change this for a demo tenant (https://demo.billyforinsurance.com/api/v1).",
    },
    {
        suffix: "billy.api.client_id",
        label: "Billy Client ID",
        help: "GUID issued by Billy for this company.",
        required: true,
    },
    {
        suffix: "billy.api.client_secret",
        label: "Billy Client Secret",
        help: "API client secret. Stored obscured.",
        secret: true,
        required: true,
    },
    {
        // The team slug, not a display name. This is the key the connector
        // actually reads (BillyApi.cs:45) and it is load-bearing: the web-session
        // reads throw without it (BillyApi_WebSession.cs:164, 292, 385) and the
        // vendor/project/contract deep links interpolate it. An earlier draft of
        // this page asked for a separate billy.company_name instead, but nothing
        // reads that key and the slug is just the slugified company name, so it
        // was a second spelling of the same fact.
        suffix: "billy.team_name",
        label: "Billy Team",
        help: "The team identifier from your Billy URL — app.billyforinsurance.com/teams/<team>. Usually your company name in lowercase with hyphens, e.g. acme-construction. Used for document downloads and links back into Billy.",
        required: true,
    },
    {
        suffix: "billy.web.enabled",
        label: "Enable Document Downloads",
        help: "When enabled, Dimension signs into the Billy web app to download certificate PDFs and attach them to compliance records.",
        type: "boolean",
    },
    {
        suffix: "billy.web.email",
        label: "Web Login Email",
        help: "Calance-owned service login, always <client>@calance.us. Invite this address as a Billy web user; we set the password.",
    },
    {
        suffix: "billy.web.password",
        label: "Web Login Password",
        help: "Password for the web user. Stored obscured.",
        secret: true,
    },
    {
        suffix: "billy.web.team_id",
        label: "Web Team ID",
        help: "Numeric team id used by the Billy web app; visible in the document URLs.",
    },
];

export const BILLY_TESTS: TestGroup[] = [
    {
        title: "Billy API",
        rows: [
            { name: "Test Load Vendors from Billy", endpath: "api/v1/vendors", result: "Not run", state: "wait" },
            { name: "Test Load Subcontracts from Billy", endpath: "api/v1/subcontracts", result: "Not run", state: "wait" },
            { name: "Test Load Requirement Groups from Billy", endpath: "api/v1/requirement_groups", result: "Not run", state: "wait" },
            { name: "Test Load Compliance Status from Billy", endpath: "api/v1/vendors/{id}/compliance", result: "Not run", state: "wait" },
        ],
    },
    {
        title: "Billy Web Session (document downloads)",
        rows: [
            { name: "Test Billy Web Login", endpath: "/users/sign_in", result: "Not run", state: "wait" },
            { name: "Test Load Vendor Documents from Billy", endpath: "/teams/{team}/vendors/{id}/documents", result: "Not run", state: "wait" },
            { name: "Test Download Certificate from Billy", endpath: "/teams/{team}/certificates/{id}.pdf", result: "Not run", state: "wait" },
        ],
    },
];

/* ---------- Intacct fields (Sage Intacct system connect) ---------- */
export const INTACCT_FIELDS: FieldDef[] = [
    {
        suffix: "intacct.api.sender_id",
        label: "Sender ID",
        help: "Always BillyMPP for Billy + Intacct clients. Not environment-specific. Must be listed in your Web Services authorizations.",
    },
    {
        suffix: "intacct.api.sender_pwd",
        label: "Sender Password",
        help: "Password paired with the sender id. Stored obscured.",
        secret: true,
        required: true,
    },
    {
        suffix: "intacct.api.login_company",
        label: "Company ID",
        help: "Your Intacct company id, exactly as typed on the Intacct sign-in screen. Differs between Implementation and Production.",
        required: true,
    },
    {
        suffix: "intacct.api.login_user",
        label: "Web Services User ID",
        help: "Created by Calance from the emailed invitation. Environment-specific.",
    },
    {
        suffix: "intacct.api.login_pwd",
        label: "Web Services Password",
        help: "Set by Calance. Stored obscured.",
        secret: true,
    },
    {
        suffix: "intacct.api.base_url",
        label: "XML Gateway URL",
        help: "Leave blank to use the default (https://api.intacct.com/ia/xml/xmlgw.phtml).",
    },
    {
        suffix: "intacct.rest.base_url",
        label: "REST Base URL",
        help: "Intacct REST API base. Leave blank to use the default.",
    },
];

export const INTACCT_TESTS: TestGroup[] = [
    {
        title: "Intacct XML Gateway",
        rows: [
            { name: "Test Login to Intacct", endpath: "getAPISession", result: "Not run", state: "wait" },
            { name: "Test Load Vendors from Intacct", endpath: "readByQuery VENDOR", result: "Not run", state: "wait" },
            { name: "Test Load Projects from Intacct", endpath: "readByQuery PROJECT", result: "Not run", state: "wait" },
            { name: "Test Load Contacts from Intacct", endpath: "readByQuery CONTACT", result: "Not run", state: "wait" },
            { name: "Test Load Compliance Types from Intacct", endpath: "readByQuery COMPLIANCETYPE", result: "Not run", state: "wait" },
            { name: "Test Load Compliance Definitions from Intacct", endpath: "readByQuery COMPLIANCEDEFINITION", result: "Not run", state: "wait" },
            { name: "Test Load Compliance Records from Intacct", endpath: "readByQuery COMPLIANCE", result: "Not run", state: "wait" },
            { name: "Test Load Subcontracts from Intacct", endpath: "readByQuery DOCUMENT", result: "Not run", state: "wait" },
        ],
    },
    {
        title: "Intacct REST API",
        rows: [
            { name: "Test Load Vendors from Intacct (REST)", endpath: "objects/accounts-payable/vendor", result: "Not run", state: "wait" },
            { name: "Test Load Projects from Intacct (REST)", endpath: "objects/projects/project", result: "Not run", state: "wait" },
            { name: "Test Load Attachments from Intacct (REST)", endpath: "objects/company-config/attachment", result: "Not run", state: "wait" },
        ],
    },
];

/* ---------- integrations (left rail "3 · Integrations" + detail view) ---------- */

export interface IntegrationDef {
    id: string;
    name: string;
    direction: string;
    desc: string;
    status: "ok" | "prog" | "todo";
    io: string;
    fields: FieldDef[];
}

/* ---------- Billy + Intacct integrations ----------
   Every key below is read by Providers/Billy_Intacct; read sites are cited in
   the help text so a wrong value can be traced back to the code that uses it. */
export const BILLY_INTEGRATIONS: IntegrationDef[] = [
    {
        id: "billy_default_behavior",
        name: "Default Behavior",
        direction: "Runtime behavior",
        desc: "Settings every Billy + Intacct integration reads",
        status: "ok",
        io: "Shared switches read by more than one integration.",
        fields: [
            {
                suffix: "integration.do_attachments",
                label: "Sync Compliance Attachments",
                help: "When on, certificate files are attached to the Intacct compliance records. Off means compliance dates and status only, no documents. Requires billy.web.enabled — the files come from the Billy web session, not the API.",
                type: "boolean",
            },
        ],
    },
    {
        id: "billy_get_vendors",
        name: "Get Vendors",
        direction: "Intacct → Billy",
        desc: "Creates and updates Billy vendors from Intacct",
        status: "prog",
        io: "Loads every Intacct vendor, keeps the active ones, applies the vendor-type lists, then matches to Billy on external_id before a fuzzy name match.",
        fields: [
            {
                suffix: "integration.vendors.default_email",
                label: "Default Contact Email",
                help: "Billy requires an email on every vendor contact. When the Intacct vendor's default contact has none, this address is used instead so the create still lands. Hard requirement — a blank value stops the run before anything loads.",
                required: true,
            },
            {
                suffix: "integration.vendor.vendor_types_getall.disallowed",
                label: "Excluded vendor types",
                help: "Intacct vendor type IDs, one per line. Removed first. Leave empty to exclude nothing.",
            },
            {
                suffix: "integration.vendor.vendor_types_getall.allowed",
                label: "Included vendor types",
                help: "Intacct vendor type IDs, one per line. Applied after excluded; leave empty to allow every remaining type. Order of operations is active-only → remove excluded → intersect included when non-empty.",
            },
        ],
    },
    {
        id: "billy_send_vendors",
        name: "Send Vendors",
        direction: "Billy → Intacct",
        desc: "Creates and updates Intacct vendors from Billy",
        status: "prog",
        io: "Sends Billy vendors to Intacct. Archived Billy vendors are never sent.",
        fields: [
            {
                suffix: "integration.vendors.default_vendor_type",
                label: "Default Intacct Vendor Type",
                help: "Vendor type assigned when Dimension creates a vendor in Intacct. If it is blank, or names a type Intacct does not have, the vendor is skipped with a warning rather than created untyped.",
                required: true,
            },
        ],
    },
    {
        id: "billy_get_projects",
        name: "Get Projects",
        direction: "Intacct → Billy",
        desc: "Projects and their subcontracts",
        status: "ok",
        io: "Get Projects, Link/Match Projects and Get Subcontracts share one setting — the entity every Intacct project query is scoped to.",
        fields: [
            {
                suffix: "integration.projects.entity",
                label: "Intacct Entity",
                help: "Multi-entity slot every project, contract, purchasing-document and subcontract read is scoped to. Blank means top level. A wrong entity is the usual reason a project load comes back empty.",
            },
        ],
    },
    {
        id: "billy_vendor_compliance",
        name: "Vendor Compliance",
        direction: "Billy → Intacct",
        desc: "Vendor-level compliance records and certificates",
        status: "prog",
        io: "Writes vendor-level compliance records to Intacct. The type mapping itself is maintained on 2 · Compliance mapping.",
        fields: [
            {
                suffix: "integration.vendor.compliance_attachment_parent_folder",
                label: "Certificate Attachment Folder",
                help: "Intacct attachment folder that vendor certificate files are filed under. Must already exist in Intacct. Only used when integration.do_attachments is on.",
            },
        ],
    },
    {
        id: "billy_subcontract_compliance",
        name: "Subcontract Compliance",
        direction: "Billy → Intacct",
        desc: "Document-level compliance on subcontracts",
        status: "todo",
        io: "Writes document-level (project / subcontract) compliance records to Intacct. The type mapping is maintained on 2 · Compliance mapping.",
        fields: [
            {
                suffix: "integration.subcontract.compliance_attachment_parent_folder",
                label: "Certificate Attachment Folder",
                help: "Intacct attachment folder for subcontract certificate files — separate from the vendor-level folder.",
            },
        ],
    },
];

export const INTEGRATIONS: IntegrationDef[] = [
    {
        id: "default_behavior",
        name: "Default Behavior",
        direction: "Runtime behavior",
        desc: "Controls how and when synced data is posted and triggered",
        status: "ok",
        io: "Controls how and when synced data is posted and triggered.",
        fields: [
            {
                suffix: "integration.cmic.transaction_post_immediately",
                label: "Post transactions immediately",
                help: "When enabled, synced transactions post automatically in CMiC.",
                type: "boolean",
            },
            {
                suffix: "integration.webhooks.should_run.bills",
                label: "Auto-sync bills from webhooks",
                help: "When enabled, Ramp bills are synced automatically upon creation/approval (criteria can be set in Bill sync settings in Ramp).",
                type: "boolean",
            },
            {
                suffix: "integration.webhooks.should_run.bill_pay",
                label: "Auto-sync bill payments from webhooks",
                help: "When enabled, Ramp bill payments are synced automatically upon creation.",
                type: "boolean",
            },
        ],
    },
];

export const MRI_INTEGRATIONS: IntegrationDef[] = [
    {
        id: "default_behavior",
        name: "Default Behavior",
        direction: "Runtime behavior",
        desc: "MRI-specific integration settings are not finalized yet",
        status: "todo",
        io: "MRI integration settings are not available yet. This section is a placeholder for upcoming Ramp + MRI controls.",
        fields: [],
    },
];

export const SPECTRUM_INTEGRATIONS: IntegrationDef[] = [
    {
        id: "default_behavior",
        name: "Default Behavior",
        direction: "Runtime behavior",
        desc: "Spectrum-specific integration settings are not finalized yet",
        status: "todo",
        io: "Spectrum integration settings are not available yet. This section is a placeholder for upcoming Ramp + Spectrum controls.",
        fields: [],
    },
];

export const JDE_INTEGRATIONS: IntegrationDef[] = [
    {
        id: "default_behavior",
        name: "Default Behavior",
        direction: "Runtime behavior",
        desc: "JDE-specific integration settings are not finalized yet",
        status: "todo",
        io: "JDE integration settings are not available yet. This section is a placeholder for upcoming Ramp + JDE controls.",
        fields: [],
    },
];
